import { getOidcMetadata } from "./oidcMetadataCache";
import { getAuthProvider } from "./providers";

const OIDC_USER_PREFIX = "oidc.user:";

let cachedOidcUserKey;
let cachedOidcPayload;
let cachedAccessToken;

const clearAccessTokenCache = () => {
  cachedOidcUserKey = undefined;
  cachedOidcPayload = undefined;
  cachedAccessToken = undefined;
};

const getLocalStorageKeys = () =>
  Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));

// Only the user stored for this app's current client. A browser that signed in
// under the previous provider still holds that provider's oidc.user entry, and
// sending its token to the API fails every call.
const findOidcUserStorageKey = () => {
  const suffix = `:${getAuthProvider().clientId()}`;
  return getLocalStorageKeys().find(
    (key) => key?.startsWith(OIDC_USER_PREFIX) && key.endsWith(suffix),
  );
};

const getOidcStorageKeys = () => {
  return getLocalStorageKeys().filter((key) => key?.startsWith("oidc."));
};

const parseAccessTokenFromPayload = (payload) => {
  if (!payload) {
    return undefined;
  }

  const parsedUserData = JSON.parse(payload);
  if (!parsedUserData || typeof parsedUserData !== "object") {
    return undefined;
  }

  return parsedUserData.access_token;
};

const parseIdTokenFromPayload = (payload) => {
  if (!payload) {
    return undefined;
  }

  const parsedUserData = JSON.parse(payload);
  if (!parsedUserData || typeof parsedUserData !== "object") {
    return undefined;
  }

  return parsedUserData.id_token;
};

export function getAccessToken() {
  try {
    // Fast path: reuse cached key/payload when OIDC storage has not changed.
    if (cachedOidcUserKey) {
      // In the mobile WebView, the native app may inject a fresh raw
      // access_token after a token refresh. If that raw key differs from the
      // cached OIDC-embedded token, the native token is newer — bust the
      // cache and return it immediately. Only applies in WebView context.
      if (globalThis.ReactNativeWebView) {
        const rawToken = localStorage.getItem("access_token");
        if (rawToken && rawToken !== cachedAccessToken) {
          clearAccessTokenCache();
          return rawToken;
        }
      }

      const currentPayload = localStorage.getItem(cachedOidcUserKey);
      if (currentPayload && currentPayload === cachedOidcPayload) {
        return cachedAccessToken;
      }

      if (currentPayload) {
        cachedOidcPayload = currentPayload;
        cachedAccessToken = parseAccessTokenFromPayload(currentPayload);
        return cachedAccessToken;
      }

      clearAccessTokenCache();
    }

    // OIDC storage key includes authority/client id, so discover it once by prefix.
    const userDataKey = findOidcUserStorageKey();

    if (!userDataKey) {
      // Fall back to the raw access_token that the native mobile app injects
      // into localStorage before the OIDC userManager has been hydrated.
      // Without this, early API calls (TopNav, Glossary) fire without an auth
      // header on first login because hydration hasn't stored the oidc.user:* key yet.
      const rawToken = localStorage.getItem("access_token");
      if (rawToken) {
        return rawToken;
      }
      clearAccessTokenCache();
      return undefined;
    }

    const userData = localStorage.getItem(userDataKey);
    if (!userData) {
      clearAccessTokenCache();
      return undefined;
    }

    cachedOidcUserKey = userDataKey;
    cachedOidcPayload = userData;
    cachedAccessToken = parseAccessTokenFromPayload(userData);

    return cachedAccessToken;
  } catch (error) {
    console.error("Error parsing access token from browser storage.", error);
    clearAccessTokenCache();
    return undefined;
  }
}

export function resetAccessTokenCache() {
  clearAccessTokenCache();
}

// Synchronous guess, made before the auth machine settles, at whether this boot
// can end signed in. Callers may only use it to delay work: a wrong "false"
// must cost a moment, never a session. Mirrors authSessionContext.evaluate():
// an unexpired user is valid, and a provider that renewsExpiredSessions renews
// an expired one at boot when it holds a refresh token.
export function hasUsableStoredSession(nowSeconds = Date.now() / 1000) {
  if (globalThis.ReactNativeWebView) {
    return true;
  }

  try {
    const key = findOidcUserStorageKey();
    const payload = key ? localStorage.getItem(key) : null;
    if (!payload) {
      return false;
    }

    const user = JSON.parse(payload);
    if (typeof user?.expires_at === "number" && user.expires_at > nowSeconds) {
      return true;
    }
    return getAuthProvider().renewsExpiredSessions && !!user?.refresh_token;
  } catch {
    return false;
  }
}

// Extracts the id_token from the same OIDC localStorage payload as the access_token.
// The id_token contains additional claims (e.g. spin_id) not present in the access_token.
export function getIdToken() {
  try {
    // Mobile WebView: native app may inject id_token directly into localStorage
    if (globalThis.ReactNativeWebView) {
      const rawIdToken = localStorage.getItem("id_token");
      if (rawIdToken) {
        return rawIdToken;
      }
    }

    const userDataKey = findOidcUserStorageKey();
    if (!userDataKey) {
      return localStorage.getItem("id_token") ?? undefined;
    }

    const userData = localStorage.getItem(userDataKey);
    return parseIdTokenFromPayload(userData);
  } catch (error) {
    console.error("Error parsing id_token from browser storage.", error);
    return undefined;
  }
}

// The token the Engage API authorizes on: the provider's apiBearer. Under Entra
// that's the ID token (see entraAuth in appsettings for why); ForgeRock's
// access token is introspected server-side.
export function getApiBearerToken() {
  return getAuthProvider().apiBearer === "id_token"
    ? getIdToken()
    : getAccessToken();
}

export function removeOidcStorageEntries() {
  getOidcStorageKeys().forEach((key) => {
    localStorage.removeItem(key);
  });

  clearAccessTokenCache();
}

// Drops users stored under any other authority or client, e.g. the ForgeRock
// session every browser still holds when an environment moves to Entra.
export function removeForeignOidcUsers(currentUserKey) {
  try {
    getLocalStorageKeys()
      .filter(
        (key) => key?.startsWith(OIDC_USER_PREFIX) && key !== currentUserKey,
      )
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Unable to remove stale OIDC users.", error);
  }
  clearAccessTokenCache();
}

export function removeAuthInfo() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expires_at");
  clearAccessTokenCache();
}

const getStoredRefreshToken = () => {
  try {
    const userDataKey = findOidcUserStorageKey();
    const payload =
      userDataKey && JSON.parse(localStorage.getItem(userDataKey));
    return payload?.refresh_token || undefined;
  } catch {
    return undefined;
  }
};

const postRevocation = async (revokeEndpoint, token, tokenTypeHint) => {
  try {
    const revokeResponse = await fetch(revokeEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        token,
        client_id: getAuthProvider().clientId(),
        token_type_hint: tokenTypeHint,
      }),
    });

    if (revokeResponse.ok) {
      console.log(`${tokenTypeHint} successfully revoked`);
    } else {
      console.warn("Token revocation failed:", revokeResponse.status);
    }
  } catch (error) {
    console.error(`Error revoking ${tokenTypeHint}:`, error);
  }
};

// Revokes this session's tokens on logout, for providers that support it (see
// revokesTokensOnLogout). Both tokens: revoking the refresh token leaves
// already-minted access tokens valid until their own exp.
export async function revokeSessionTokens() {
  try {
    const provider = getAuthProvider();
    if (!provider.revokesTokensOnLogout) {
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      console.warn("No access token found to revoke");
      return;
    }

    // In the mobile WebView the refresh token is the native app's (injected
    // from its secure store); revoking it would end the native session.
    const refreshToken = globalThis.ReactNativeWebView
      ? undefined
      : getStoredRefreshToken();

    // The discovery document the app cached at boot (fetched only if missing).
    const config = await getOidcMetadata(provider.wellKnownUrl);
    const revokeEndpoint = config.revocation_endpoint;
    if (!revokeEndpoint) {
      console.warn("No revocation_endpoint in well-known metadata; skipping.");
      return;
    }

    await Promise.all([
      refreshToken &&
        postRevocation(revokeEndpoint, refreshToken, "refresh_token"),
      postRevocation(revokeEndpoint, accessToken, "access_token"),
    ]);
  } catch (error) {
    console.error("Error during token revocation:", error);
  }
}
